import { Component, OnInit, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { DocTypeVersionService } from '../../../core/services/doc-type-version.service';
import { DocTypeVersion, ProjectDocReferential } from '../../../core/models/models';
import { ConfirmationDialogComponent } from '../../../shared/components/dialogs/confirmation-dialog.component';
import { CreateDocVersionDialogComponent } from './create-doc-version-dialog.component';

@Component({
  selector: 'app-doc-type-version',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatIconModule, ConfirmationDialogComponent, CreateDocVersionDialogComponent],
  templateUrl: './doc-type-version.component.html',
  styleUrl: './doc-type-version.component.scss'
})
export class DocTypeVersionComponent implements OnInit {
  private service = inject(DocTypeVersionService);
  private fb = inject(FormBuilder);

  versions = signal<DocTypeVersion[]>([]);
  selectedVersion = signal<DocTypeVersion | null>(null);
  errorMessage = signal('');
  successMessage = signal('');
  loading = signal(false);
  isSidebarCollapsed = signal(false);

  // Dialog création nouvelle version
  showCreateDialog = false;

  // Édition type de document
  editingDocType = signal<ProjectDocReferential | null>(null);
  showAddDocTypeForm = false;
  showDeleteConfirm = signal(false);
  deletingDocType = signal<ProjectDocReferential | null>(null);
  canDeleteDocType = signal(false);
  showValidateConfirm = signal(false);

  addDocTypeForm = this.fb.group({
    name: ['', Validators.required],
    maxDocs: [null as number | null],
    mandatory: [false],
    commentMandatory: [false],
    docReferenceMandatory: [false],
    commentPlaceholder: ['']
  });

  editDocTypeForm = this.fb.group({
    name: ['', Validators.required],
    maxDocs: [null as number | null],
    mandatory: [false],
    commentMandatory: [false],
    docReferenceMandatory: [false],
    commentPlaceholder: ['']
  });

  ngOnInit(): void {
    this.loadVersions();
  }

  loadVersions(selectId?: number): void {
    this.service.getAllVersions().subscribe({
      next: (data) => {
        this.versions.set(data);
        if (selectId) {
          const toSelect = data.find(v => v.id === selectId);
          if (toSelect) { this.selectedVersion.set(toSelect); return; }
        }
        if (!this.selectedVersion() && data.length > 0) {
          this.selectedVersion.set(data[0]);
        } else if (this.selectedVersion()) {
          // Rafraîchir la version sélectionnée
          const refreshed = data.find(v => v.id === this.selectedVersion()!.id);
          if (refreshed) this.selectedVersion.set(refreshed);
        }
      },
      error: () => this.errorMessage.set('Erreur lors du chargement des versions')
    });
  }

  toggleSidebar(): void {
    this.isSidebarCollapsed.update(v => !v);
  }

  selectVersion(v: DocTypeVersion): void {
    this.selectedVersion.set(v);
    this.cancelAddDocType();
    this.cancelEditDocType();
  }

  get canCreateNewVersion(): boolean {
    return !this.selectedVersion() || !!this.selectedVersion()?.validated;
  }

  // --- Création nouvelle version via dialog ---

  openCreateDialog(): void {
    this.showCreateDialog = true;
  }

  handleCreateConfirmed(type: 'scratch' | 'duplicate'): void {
    this.showCreateDialog = false;
    this.loading.set(true);

    if (type === 'duplicate' && this.selectedVersion()?.id) {
      this.service.duplicateVersion(this.selectedVersion()!.id!).subscribe({
        next: (v) => {
          this.successMessage.set(`Version "${v.version}" créée par duplication.`);
          this.loading.set(false);
          this.loadVersions(v.id);
        },
        error: (err) => {
          this.errorMessage.set(err?.error?.message || 'Erreur lors de la duplication');
          this.loading.set(false);
        }
      });
    } else {
      this.service.createVersion().subscribe({
        next: (v) => {
          this.successMessage.set(`Version "${v.version}" créée.`);
          this.loading.set(false);
          this.loadVersions(v.id);
        },
        error: (err) => {
          this.errorMessage.set(err?.error?.message || 'Erreur lors de la création');
          this.loading.set(false);
        }
      });
    }
  }

  // --- Gestion types de documents ---

  openAddDocType(): void {
    this.showAddDocTypeForm = true;
    this.addDocTypeForm.reset();
    this.cancelEditDocType();
  }

  cancelAddDocType(): void {
    this.showAddDocTypeForm = false;
    this.addDocTypeForm.reset();
  }

  submitAddDocType(): void {
    const ver = this.selectedVersion();
    if (!ver?.id || this.addDocTypeForm.invalid) return;
    const data = this.addDocTypeForm.value as ProjectDocReferential;
    this.service.addDocType(ver.id, data).subscribe({
      next: (updated) => {
        this.selectedVersion.set(updated);
        this.cancelAddDocType();
        this.successMessage.set('Type de document ajouté.');
        this.loadVersions();
      },
      error: () => this.errorMessage.set('Erreur lors de l\'ajout du type de document')
    });
  }

  openEditDocType(ref: ProjectDocReferential): void {
    this.editingDocType.set(ref);
    this.editDocTypeForm.patchValue({
      name: ref.name,
      maxDocs: ref.maxDocs,
      mandatory: ref.mandatory,
      commentMandatory: ref.commentMandatory,
      docReferenceMandatory: ref.docReferenceMandatory,
      commentPlaceholder: ref.commentPlaceholder
    });
    this.cancelAddDocType();
  }

  cancelEditDocType(): void {
    this.editingDocType.set(null);
    this.editDocTypeForm.reset();
  }

  submitEditDocType(): void {
    const ref = this.editingDocType();
    if (!ref?.id || this.editDocTypeForm.invalid) return;
    const data = this.editDocTypeForm.value as ProjectDocReferential;
    this.service.updateDocType(ref.id, data).subscribe({
      next: () => {
        this.cancelEditDocType();
        this.successMessage.set('Type de document mis à jour.');
        this.loadVersions();
      },
      error: () => this.errorMessage.set('Erreur lors de la mise à jour')
    });
  }

  requestDeleteDocType(ref: ProjectDocReferential): void {
    if (!ref.id) return;
    this.service.canDeleteDocType(ref.id).subscribe({
      next: (can) => {
        this.canDeleteDocType.set(can);
        this.deletingDocType.set(ref);
        this.showDeleteConfirm.set(true);
      },
      error: () => this.errorMessage.set('Erreur lors de la vérification')
    });
  }

  confirmDeleteDocType(): void {
    const ref = this.deletingDocType();
    if (!ref?.id) return;
    this.service.deleteDocType(ref.id).subscribe({
      next: () => {
        this.showDeleteConfirm.set(false);
        this.deletingDocType.set(null);
        this.successMessage.set('Type de document supprimé/archivé.');
        this.loadVersions();
      },
      error: () => this.errorMessage.set('Erreur lors de la suppression')
    });
  }

  cancelDeleteDocType(): void {
    this.showDeleteConfirm.set(false);
    this.deletingDocType.set(null);
  }

  requestValidateVersion(): void {
    this.showValidateConfirm.set(true);
  }

  confirmValidateVersion(): void {
    const ver = this.selectedVersion();
    if (!ver?.id) return;
    this.service.validateVersion(ver.id).subscribe({
      next: (updated) => {
        this.showValidateConfirm.set(false);
        this.successMessage.set(`Version "${updated.version}" validée. Les modifications sont désormais bloquées.`);
        this.loadVersions();
        this.selectedVersion.set(updated);
      },
      error: (err) => {
        this.showValidateConfirm.set(false);
        this.errorMessage.set(err?.error?.message || err?.error || 'Erreur lors de la validation');
      }
    });
  }

  cancelValidateVersion(): void {
    this.showValidateConfirm.set(false);
  }

  getDeleteTitle(): string {
    return this.canDeleteDocType() ? 'Supprimer le type de document' : 'Archiver le type de document';
  }

  getDeleteMessage(): string {
    const name = this.deletingDocType()?.name ?? '';
    return this.canDeleteDocType()
      ? `Voulez-vous supprimer définitivement le type "${name}" ?`
      : `Le type "${name}" est lié à des documents. Voulez-vous l'archiver ?`;
  }
}
